/**
 * Learner Detail Page
 * Shows learner information and all their submissions across all assignments
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Paper, Typography, Button, Alert, CircularProgress,
  Breadcrumbs, Link as MuiLink, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Chip, IconButton, Tooltip,
  Card, CardContent, Divider, Stack
} from '@mui/material';
import {
  Home, Edit, Visibility, Person, Email, School, Grade as GradeIcon
} from '@mui/icons-material';
import axios from 'axios';

interface Learner {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  student_id: string;
  grade_level: string;
  school_id: number;
  school_name: string;
  is_active: boolean;
}

interface Submission {
  id: number;
  assessment_id: number;
  assessment_title: string;
  original_filename: string;
  status: string;
  ai_score: number | null;
  teacher_score: number | null;
  submitted_at: string;
  is_active: boolean;
}

const LearnerDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  // Data state
  const [learner, setLearner] = useState<Learner | null>(null);
  const [submissions, setSubmissions] = useState<Submission[]>([]);

  // UI state
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Load learner and their submissions
  useEffect(() => {
    const fetchData = async () => {
      if (!id) return;

      try {
        setLoading(true);
        setError(null);

        const [learnerRes, submissionsRes] = await Promise.all([
          axios.get(`/api/learners/${id}/`),
          axios.get(`/api/submissions/?learner=${id}&page_size=1000`),
        ]);

        setLearner(learnerRes.data);
        setSubmissions(submissionsRes.data.results || []);
      } catch (err: any) {
        console.error('Error loading learner data:', err);
        setError(err.response?.data?.error || 'Failed to load learner information');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id]);

  // Get status color
  const getStatusColor = (status: string): "success" | "warning" | "error" | "info" => {
    switch (status) {
      case 'marked': return 'success';
      case 'pending': return 'warning';
      case 'review': return 'info';
      default: return 'info';
    }
  };

  // Calculate statistics
  const totalSubmissions = submissions.length;
  const markedSubmissions = submissions.filter(s => s.status === 'marked').length;
  const pendingSubmissions = submissions.filter(s => s.status === 'pending').length;

  const scores = submissions
    .map(s => s.teacher_score ?? s.ai_score)
    .filter(s => s !== null) as number[];

  const averageScore = scores.length > 0
    ? (scores.reduce((a, b) => a + b, 0) / scores.length).toFixed(1)
    : 'N/A';

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (!learner) {
    return (
      <Box>
        <Alert severity="error">Learner not found</Alert>
      </Box>
    );
  }

  return (
    <Box>
      {/* Breadcrumbs */}
      <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
        <MuiLink
          component="button"
          variant="body1"
          onClick={() => navigate('/dashboard')}
          sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 0.5 }}
        >
          <Home fontSize="small" />
          Dashboard
        </MuiLink>
        <MuiLink
          component="button"
          variant="body1"
          onClick={() => navigate('/learners')}
          sx={{ cursor: 'pointer' }}
        >
          Learners
        </MuiLink>
        <Typography color="text.primary">
          {learner.first_name} {learner.last_name}
        </Typography>
      </Breadcrumbs>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Learner Information Card */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="start" mb={3}>
          <Box display="flex" alignItems="center" gap={2}>
            <Person sx={{ fontSize: 48, color: 'primary.main' }} />
            <Box>
              <Typography variant="h4">
                {learner.first_name} {learner.last_name}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Student ID: {learner.student_id}
              </Typography>
            </Box>
          </Box>
          <Button
            variant="outlined"
            startIcon={<Edit />}
            onClick={() => navigate(`/learners/${learner.id}/edit`)}
          >
            Edit Learner
          </Button>
        </Box>

        <Divider sx={{ my: 2 }} />

        <Stack direction="row" spacing={3} flexWrap="wrap">
          <Box sx={{ minWidth: 200 }}>
            <Stack spacing={1}>
              <Box display="flex" alignItems="center" gap={1}>
                <Email fontSize="small" color="action" />
                <Typography variant="body2" color="textSecondary">Email</Typography>
              </Box>
              <Typography variant="body1">{learner.email}</Typography>
            </Stack>
          </Box>

          <Box sx={{ minWidth: 150 }}>
            <Stack spacing={1}>
              <Box display="flex" alignItems="center" gap={1}>
                <GradeIcon fontSize="small" color="action" />
                <Typography variant="body2" color="textSecondary">Grade Level</Typography>
              </Box>
              <Typography variant="body1">{learner.grade_level || 'Not specified'}</Typography>
            </Stack>
          </Box>

          <Box sx={{ minWidth: 200 }}>
            <Stack spacing={1}>
              <Box display="flex" alignItems="center" gap={1}>
                <School fontSize="small" color="action" />
                <Typography variant="body2" color="textSecondary">School</Typography>
              </Box>
              <Typography variant="body1">{learner.school_name}</Typography>
            </Stack>
          </Box>

          <Box sx={{ minWidth: 100 }}>
            <Stack spacing={1}>
              <Typography variant="body2" color="textSecondary">Status</Typography>
              <Chip
                label={learner.is_active ? 'Active' : 'Inactive'}
                color={learner.is_active ? 'success' : 'default'}
                size="small"
              />
            </Stack>
          </Box>
        </Stack>
      </Paper>

      {/* Statistics Cards */}
      <Stack direction="row" spacing={2} sx={{ mb: 3, flexWrap: 'wrap' }}>
        <Card sx={{ flex: '1 1 200px', minWidth: 200 }}>
          <CardContent>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              Total Submissions
            </Typography>
            <Typography variant="h4" color="primary">
              {totalSubmissions}
            </Typography>
          </CardContent>
        </Card>

        <Card sx={{ flex: '1 1 200px', minWidth: 200 }}>
          <CardContent>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              Marked
            </Typography>
            <Typography variant="h4" color="success.main">
              {markedSubmissions}
            </Typography>
          </CardContent>
        </Card>

        <Card sx={{ flex: '1 1 200px', minWidth: 200 }}>
          <CardContent>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              Pending
            </Typography>
            <Typography variant="h4" color="warning.main">
              {pendingSubmissions}
            </Typography>
          </CardContent>
        </Card>

        <Card sx={{ flex: '1 1 200px', minWidth: 200 }}>
          <CardContent>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              Average Score
            </Typography>
            <Typography variant="h4" color="primary">
              {averageScore}
            </Typography>
          </CardContent>
        </Card>
      </Stack>

      {/* Submissions Table */}
      <Paper>
        <Box p={2} borderBottom={1} borderColor="divider">
          <Typography variant="h6">Submissions</Typography>
        </Box>

        {submissions.length === 0 ? (
          <Box p={4} textAlign="center">
            <Typography variant="body2" color="textSecondary">
              No submissions yet
            </Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>Assignment</TableCell>
                  <TableCell>File Name</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">AI Score</TableCell>
                  <TableCell align="right">Teacher Score</TableCell>
                  <TableCell align="right">Final Score</TableCell>
                  <TableCell>Submitted</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {submissions.map((submission) => {
                  const finalScore = submission.teacher_score ?? submission.ai_score;
                  return (
                    <TableRow key={submission.id} hover>
                      <TableCell>{submission.id}</TableCell>
                      <TableCell>{submission.assessment_title}</TableCell>
                      <TableCell>{submission.original_filename}</TableCell>
                      <TableCell>
                        <Chip
                          label={submission.status}
                          size="small"
                          color={getStatusColor(submission.status)}
                        />
                      </TableCell>
                      <TableCell align="right">
                        {submission.ai_score !== null ? Number(submission.ai_score).toFixed(1) : '-'}
                      </TableCell>
                      <TableCell align="right">
                        {submission.teacher_score !== null ? Number(submission.teacher_score).toFixed(1) : '-'}
                      </TableCell>
                      <TableCell align="right">
                        <Typography fontWeight="bold">
                          {finalScore !== null ? Number(finalScore).toFixed(1) : '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {submission.submitted_at
                          ? new Date(submission.submitted_at).toLocaleDateString()
                          : '-'}
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="View/Mark submission">
                          <IconButton
                            size="small"
                            onClick={() => navigate(`/submissions/${submission.id}/mark`)}
                          >
                            <Visibility fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
    </Box>
  );
};

export default LearnerDetailPage;
